import { createRouter, createWebHistory } from "vue-router";
import MainView from "../views/MainView.vue";
import PlatformView from "../views/PlatformView.vue";
import TaskView from "../views/TaskView.vue";
import WechatView from "../views/WechatView.vue";
import WechatDetailView from "../views/WechatDetailView.vue";
import SubscriptionView from "../views/SubscriptionView.vue";
import AdminView from "../views/AdminView.vue";
import NoAccessView from "../views/NoAccessView.vue";
import ChatHistoryView from "../views/ChatHistoryView.vue";
import { getAuthToken, getQuery } from "../api/util";
import { currentUser, loadMe, canViewAccounts } from "../api/currentUser";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "main",
      component: MainView,
      meta: {
        title: '微信管理平台 - 主页',
        keepAlive: true,
        requiresAdmin: true,
      }
    },
    {
      path: "/platform",
      name: "platform",
      component: PlatformView,
      meta: {
        title: '微信管理平台 - 平台',
        keepAlive: true,
        requiresCreate: true,
      }
    },
    {
      path: "/wechat",
      name: "wechat",
      component: WechatView,
      meta: {
        title: '微信管理平台 - 微信账号',
        keepAlive: true,
        requiresAccounts: true,
      }
    },
    {
      path: "/wechat_detail/:wcId",
      name: "wechat_detail",
      component: WechatDetailView,
      meta: {
        title: '微信管理平台 - 账号详情',
        keepAlive: true,
      }
    },
    {
      path: "/chat_history",
      name: "chat_history",
      component: ChatHistoryView,
      meta: {
        title: '微信管理平台 - 聊天记录',
        keepAlive: true,
        requiresAccounts: true,
      }
    },
    {
      path: "/task",
      name: "task",
      component: TaskView,
      meta: {
        title: '微信管理平台 - 任务',
        keepAlive: true,
        requiresAdmin: true,
      }
    },
    {
      path: "/subscription",
      name: "subscription",
      component: SubscriptionView,
      meta: {
        title: '微信管理平台 - 订阅消息',
        keepAlive: true,
        requiresAdmin: true,
      }
    },
    {
      path: "/admin",
      name: "admin",
      component: AdminView,
      meta: {
        title: '微信管理平台 - 用户管理',
        keepAlive: false,
        requiresAdmin: true,
      }
    },
    {
      path: "/no-access",
      name: "no-access",
      component: NoAccessView,
      meta: {
        title: '微信管理平台',
        keepAlive: false,
      }
    },
  ],
});

// The best screen to land a user on given their permissions.
function landingRoute(): { name: string } {
  if (currentUser.isAdmin) return { name: "main" };
  if (canViewAccounts()) return { name: "wechat" };
  if (currentUser.canCreateAccount) return { name: "platform" };
  return { name: "no-access" };
}

router.beforeEach(async (to, _from, next) => {
  if (to.meta.title) {
    document.title = to.meta.title as string;
  }

  // App.vue is responsible for completing the CAS handshake when
  // ?ticket=... is present, so let those navigations through.
  const query = getQuery();
  if (query["ticket"] && query["ticket"].length > 0) {
    next();
    return;
  }

  if (!getAuthToken()) {
    // Mirror the behavior previously in App.vue's onMounted: bounce to
    // CAS without rendering any of the protected views first.
    window.location.href =
      import.meta.env.VITE_CAS_LOGIN + "/login?service=" + import.meta.env.VITE_APP_URL;
    return;
  }

  // Role gating. The backend is the real enforcer (filters lists, 403s
  // unauthorized ops); this keeps operators out of screens they can't use and
  // sends a zero-permission user to the no-access page. Load perms on demand.
  if (to.meta.requiresAdmin || to.meta.requiresCreate || to.meta.requiresAccounts) {
    if (!currentUser.loaded) await loadMe();
    let allowed = false;
    if (to.meta.requiresAdmin) allowed = currentUser.isAdmin;
    else if (to.meta.requiresCreate) allowed = currentUser.isAdmin || currentUser.canCreateAccount;
    else if (to.meta.requiresAccounts) allowed = canViewAccounts();
    if (!allowed) {
      const target = landingRoute();
      if (to.name === target.name) next();
      else next(target);
      return;
    }
  }

  next();
});

export default router;
